import { Box, Button, Checkbox, Divider, FormControlLabel, Paper, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import HandleOperation from 'components/utils/HandleOperation';
import { ReactNode, ChangeEvent, useMemo, Fragment, Dispatch, SetStateAction } from 'react';
import Filter2LineIcon from 'remixicon-react/Filter2LineIcon';
import FilterOptions from './FilterOptions';

export type FilterOptionType = {
  label: ReactNode;
  badgeValue?: ReactNode;
  value: string;
  disabled?: boolean | undefined;
};

export type ClearFiltersOptions = {
  defaultClearFiltersCallback: () => void;
};

export type FilterType = {
  category: string;
  categoryIndex: number;
  displayName?: string;
  options: FilterOptionType[];
  inputType?: string;
  allOptions?: string[];
  defaultOpen?: boolean;
  isGrouped?: boolean;
  // checkbox type input options
  showSelectAll?: boolean;
  selectedOptions?: string[];
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    options: {
      categoryIndex: number;
      defaultCheckboxChangeCallback: () => void;
    },
  ) => void;
  // radio type input options
  selectedValue?: string;
  defaultSelectedValue?: string;
  onRadioChange?: (
    event: ChangeEvent<HTMLInputElement>,
    options: {
      categoryIndex: number;
      defaultRadioChangeCallback: () => void;
    },
  ) => void;
};

type FilterProps = {
  filters: FilterType[];
  isLoading?: boolean;
  skeltonCount?: number;
  clearFilters?: (options: ClearFiltersOptions) => void;
  handleFiltersChange?: (filters: FilterType[]) => void;
  setFilters: Dispatch<SetStateAction<FilterType[]>>;
};

const Filter: React.FC<FilterProps> = ({
  filters,
  isLoading,
  skeltonCount,
  clearFilters,
  handleFiltersChange,
  setFilters,
}) => {
  const [individualFilters, groupedFilters] = useMemo(() => {
    const directFilters: FilterType[] = [];
    const groupedFilters: FilterType[] = [];

    filters.forEach((filter) => {
      if (filter.isGrouped !== undefined && !filter.isGrouped) {
        directFilters.push(filter);
      } else if (filter.options?.length) {
        groupedFilters.push(filter);
      }
    });

    return [directFilters, groupedFilters];
  }, [filters]);
  const filtersSelected = useMemo(
    () =>
      filters.filter((filter) =>
        filter.inputType === 'radio'
          ? filter.selectedValue !== filter.defaultSelectedValue
          : filter.selectedOptions && filter.selectedOptions?.length > 0,
      ).length,
    [filters],
  );

  const getOptions = (options: FilterOptionType[]) => {
    const blankOptionIndex = options.findIndex((option) => option.value === '(blank)');
    if (blankOptionIndex > -1) {
      const updatedOptions = [...options];
      updatedOptions.splice(blankOptionIndex, 1);
      return [...updatedOptions, options[blankOptionIndex]];
    }
    return options;
  };

  function handleCheckboxChange(
    categoryIndex: number,
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) {
    setFilters((prevState) => {
      const updatedState = [...prevState];

      if (event.target.value === 'Select All') {
        if (checked) {
          updatedState[categoryIndex].selectedOptions = updatedState[categoryIndex].allOptions;
        } else {
          updatedState[categoryIndex].selectedOptions = [];
        }
      } else {
        if (checked) {
          updatedState[categoryIndex].selectedOptions = [
            ...(updatedState[categoryIndex]?.selectedOptions || []),
            event.target.value,
          ];
        } else {
          updatedState[categoryIndex].selectedOptions = updatedState[
            categoryIndex
          ]?.selectedOptions?.filter((option) => option !== event.target.value);
        }
      }

      handleFiltersChange?.(updatedState);
      return updatedState;
    });
  }

  function handleRadioChange(categoryIndex: number, event: ChangeEvent<HTMLInputElement>) {
    setFilters((prevState) => {
      const updatedState = [...prevState];

      if (event.target.checked) {
        updatedState[categoryIndex].selectedValue = event.target.value;
      }
      handleFiltersChange?.(updatedState);
      return updatedState;
    });
  }

  function handleClearFilters() {
    setFilters((prevState) => {
      const updatedState = prevState.map((filter) => ({
        ...filter,
        selectedOptions: [],
        selectedValue: filter.defaultSelectedValue,
        defaultSelectedValue: filter.defaultSelectedValue,
      }));

      handleFiltersChange?.(updatedState);
      return updatedState;
    });
  }

  const onCheckboxChange = (
    filter: FilterType,
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (filter.onChange) {
      filter.onChange(event, checked, {
        categoryIndex: filter.categoryIndex,
        defaultCheckboxChangeCallback: handleCheckboxChange.bind(
          null,
          filter.categoryIndex,
          event,
          checked,
        ),
      });
    } else {
      handleCheckboxChange(filter.categoryIndex, event, checked);
    }
  };

  const onRadioChange = (filter: FilterType, event: ChangeEvent<HTMLInputElement>) => {
    if (filter.onRadioChange) {
      filter.onRadioChange(event, {
        categoryIndex: filter.categoryIndex,
        defaultRadioChangeCallback: handleRadioChange.bind(null, filter.categoryIndex, event),
      });
    } else {
      handleRadioChange(filter.categoryIndex, event);
    }
  };

  const onClearFilters = () => {
    if (clearFilters) {
      clearFilters({
        defaultClearFiltersCallback: handleClearFilters,
      });
    } else {
      handleClearFilters();
    }
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <$StickyBox>
      <$Filter>
        <$FilterHeader>
          <Box display="flex">
            <Filter2LineIcon style={{ marginRight: '8px' }} />
            Filter {!!filtersSelected && `(${filtersSelected})`}
          </Box>
          {clearFilters && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              visibility={filtersSelected ? 'visible' : 'hidden'}
            >
              <Button onClick={onClearFilters}>Clear Filters</Button>
            </Box>
          )}
        </$FilterHeader>
        <Divider sx={{ mx: 2 }} />
        <$Container>
          <HandleOperation
            isLoading={isLoading}
            skelton={
              <Box pt={1} my={1} display="flex" alignItems="center">
                <Skeleton variant="rounded" width="100%" height={15} />
              </Box>
            }
            skeltonCount={skeltonCount || 5}
          >
            {individualFilters.map((filter, index) => (
              <Box sx={{ pt: 2 }} key={index}>
                {filter.options.map((option, index) => (
                  <Box key={`${index}${option.label}`} width="100%" sx={{ px: 1 }}>
                    <$FormControlLabel
                      control={
                        <Checkbox
                          onChange={(...params) => onCheckboxChange(filter, ...params)}
                          checked={filter?.selectedOptions?.includes(option.value)}
                          value={option.value}
                        />
                      }
                      label={
                        <Box component="span" width="100%" display="flex" alignItems="center">
                          {option.label} &nbsp;
                          {option.badgeValue ? `(${option.badgeValue})` : ''}
                        </Box>
                      }
                      value={option.value}
                      disabled={option.disabled}
                    />
                  </Box>
                ))}
              </Box>
            ))}
            {groupedFilters.map((filter, index) => (
              <Fragment key={filter.category}>
                <FilterOptions
                  filter={filter}
                  onCheckboxChange={onCheckboxChange}
                  onRadioChange={onRadioChange}
                />
                {index < groupedFilters.length - 1 && <Divider />}
              </Fragment>
            ))}
          </HandleOperation>
        </$Container>
      </$Filter>
    </$StickyBox>
  );
};

const $StickyBox = styled(Box, { label: 'StickyBox' })(({ theme }) => ({
  position: 'sticky',
  top: 150,
  zIndex: 9,
  maxHeight: '80vh',
  overflow: 'hidden',
  '&:hover': {
    overflow: 'auto',
  },
}));

const $Filter = styled(Paper, { label: 'Filter' })(({ theme }) => ({
  width: '100%',
  background: 'transparent',
  borderRadius: 8,
  boxShadow: 'none',
}));

const $FilterHeader = styled(Box, { label: 'FilterHeader' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 'bold',
  padding: '8px 20px',
}));

const $Container = styled(Box, { label: 'Container' })({
  position: 'relative',
  padding: '0 16px 8px',
});

export const $FormControlLabel = styled(FormControlLabel, { label: 'FormControlLabel' })({
  width: '100%',
  alignItems: 'flex-start',
  marginTop: '2px',
  marginBottom: '2px',
  '& .MuiFormControlLabel-label': {
    width: '100%',
    fontSize: 15,
  },
  '&.select-all .MuiFormControlLabel-label': {
    fontWeight: 'bold',
    userSelect: 'none',
  },
  '& .MuiCheckbox-root, & .MuiRadio-root': {
    marginTop: -8,
  },
});

export default Filter;
